<template>
  <ion-modal
    :is-open="isOpen"
    css-class="file-viewer-modal"
    aria-label="File viewer"
    @will-present="onWillPresent"
    @did-dismiss="onDidDismiss"
  >
    <!-- Keyed by generation so a re-open lands on a fresh viewer (fresh zoom,
         fresh load state) instead of patching props into the old one. `shown`
         outlives the singleton's request by one dismiss animation so the
         content does not vanish mid-slide. -->
    <FileViewerModal
      v-if="shown"
      :key="shown.generation"
      :files="shown.files"
      :index="shown.index"
      :resolve="shown.resolve"
      :generation="shown.generation"
      @close="closeFileViewer"
    />
  </ion-modal>
</template>

<script setup lang="ts">
/**
 * Host for the shared file viewer — the ONE `<ion-modal>` behind
 * `openFileViewer()` (services/file-viewer.ts).
 *
 * Mounted in App.vue and deliberately LAST inside `<ion-app>`:
 *  - Ionic dismisses the last presented overlay in DOCUMENT order on hardware
 *    back / Escape, so being last is what makes Back close the viewer rather
 *    than the modal (or page) underneath it.
 *  - The very first overlay element connected in the document is excluded from
 *    that lookup entirely (`overlayIndex > 0`), so a Host must never be first.
 *
 * A Host mounted directly under `<ion-app>` also paints above any modal a page
 * opens — `ion-router-outlet` is its own `z-index: 0` stacking context — which
 * is what lets Notes / To-Do comments / Disciplinary (themselves modals) open
 * the viewer over the top of themselves.
 *
 * `backdrop-dismiss` is left at its default and `can-dismiss` is not set: in
 * Ionic 8.8 the hardware-back handler registers only while backdropDismiss is
 * true — with it false, the router's priority-0 handler wins and Back would
 * navigate the page away underneath the still-open viewer.
 */
import { computed, defineAsyncComponent, shallowRef, watch } from 'vue';
import { IonModal } from '@ionic/vue';
import { closeFileViewer, fileViewerRequest, type FileViewerRequest } from '@/services/file-viewer';
import { restoreDefaultBars, useDarkSurfaceBars } from '@/services/device/system-bars.service';

// The viewer (and Swiper with it) loads on the first open, not at app boot —
// this Host is mounted for every session, most of which never view a file.
const FileViewerModal = defineAsyncComponent(() => import('@/components/modals/FileViewerModal.vue'));

const isOpen = computed(() => fileViewerRequest.value !== null);

/** The request being displayed; kept through the dismiss animation. */
const shown = shallowRef<FileViewerRequest | null>(null);
watch(
  fileViewerRequest,
  (req) => {
    if (req) shown.value = req;
  },
  { immediate: true },
);

function onWillPresent(): void {
  // Light status-bar icons over the dark surface (native only; no-op on web).
  void useDarkSurfaceBars();
}

function onDidDismiss(): void {
  // Sync the singleton when the dismissal came from Back / Escape rather than
  // from the viewer's own ✕ (idempotent either way).
  closeFileViewer();
  shown.value = null;
  void restoreDefaultBars();
  unhideOutletIfPageModalRemains();
}

/**
 * Ionic un-hides `ion-router-outlet` only when the LAST focus-trapping overlay
 * dismisses. Page-modal → viewer → dismiss viewer therefore leaves the outlet
 * `aria-hidden="true"` with the page modal — which lives INSIDE the outlet —
 * still open, making it silent to TalkBack / VoiceOver until it too closes.
 * Restoring the pre-viewer state (an inline page modal never hides the outlet
 * in the first place) is one attribute removal.
 */
function unhideOutletIfPageModalRemains(): void {
  const outlet = document.querySelector('ion-router-outlet');
  if (!outlet) return;
  const pageModalStillOpen = outlet.querySelector('ion-modal.show-modal') !== null;
  if (pageModalStillOpen) outlet.removeAttribute('aria-hidden');
}
</script>

<!-- Non-scoped: the Ionic modal renders in a portal outside this component.
     The shell rules live HERE, in the eager Host, rather than in the
     lazy-loaded viewer: the modal presents the instant `is-open` flips, and on
     a tablet Ionic's default 600×500 card would flash before the viewer's
     chunk (and its CSS) arrived. -->
<style lang="scss">
ion-modal.file-viewer-modal {
  /* Literal 100% — anything else (even calc(100% - 0px)) makes Ionic treat the
     modal as custom-sized and zero out --ion-safe-area-*, which slides the ✕
     under the notch. All six are load-bearing: at ≥768×600 (tablets pass the
     mobile gate) Ionic otherwise resets to a 600×500 rounded card. */
  --width: 100%;
  --max-width: 100%;
  --height: 100%;
  --max-height: 100%;
  --border-radius: 0;
  --box-shadow: none;
  --background: #000000;
}

/* Belt-and-braces: Ionic ships `ion-modal>.ion-page{height:100%}`, but a
   Swiper collapses to 0px the moment this ever resolves to auto. */
ion-modal.file-viewer-modal .ion-page {
  height: 100%;
}
</style>
